/**
 * components/landing/home/FAQ.jsx — Section 13.
 *
 * Accordion with the 8 mandatory FAQ items from the Phase 2 PRD.
 * Uses HTML <details>/<summary> for zero-JS accessibility, with a subtle
 * animated caret and shadcn-flavoured styling.
 */

import { Container, Eyebrow, SectionHeading } from '../design-system'
import { ChevronDown } from 'lucide-react'

const ITEMS = [
  {
    q: 'What is Kalki Ai Cloud?',
    a: 'Kalki Ai Cloud is an enterprise AI trading SaaS platform.  It combines AI intelligence, professional trading tools, education, automation, subscriptions, and White-Label deployment into one unified product family.  Businesses use it to launch and operate their own branded AI trading platform.',
  },
  {
    q: 'How does White-Label licensing work?',
    a: 'You purchase a license, configure your brand + domain, and Kalki Ai Cloud provisions a fully-branded tenant running on our platform.  You own the customer relationship; Kalki Ai Cloud manages platform evolution, security, and infrastructure.',
  },
  {
    q: 'Can I use my own domain?',
    a: 'Yes.  Every plan supports a custom domain (e.g. platform.yourbrand.com) with automatic SSL and CDN.  DNS setup takes a few minutes.',
  },
  {
    q: 'Is Stripe supported?',
    a: 'Yes.  Stripe is supported natively (subscriptions, invoicing, coupons, add-ons, metering) along with NowPayments for cryptocurrency.  You can configure per-tenant billing profiles.',
  },
  {
    q: 'What modules are included in every plan?',
    a: 'Every plan includes the full stack: AI Mentor, Trading Terminal, Strategy Builder, Backtesting, Academy, Automation, Analytics, Billing, and White-Label configuration.  There are no feature paywalls between plans — the difference is the license duration.',
  },
  {
    q: 'Do I receive platform updates?',
    a: 'Yes.  Continuous updates ship to every tenant automatically.  Feature flags let you control the rollout inside your workspace so your customers only see what you’re ready to expose.',
  },
  {
    q: 'Can I upgrade plans mid-cycle?',
    a: 'Yes.  Upgrading is prorated automatically.  Downgrades take effect at the next renewal.',
  },
  {
    q: 'How long does setup take?',
    a: 'Typical launches complete in 2–4 weeks including branding, domain, KYC, and configuration.  Simple pilots can go live in as little as 7 days.',
  },
]

const FAQ = () => (
  <section className="py-24 md:py-40">
    <Container size="narrow">
      <SectionHeading
        eyebrow="FAQ"
        title="Answers to the questions operators ask us most."
        subtitle="Missing something?  Talk to us — we answer every enquiry within one business day."
        align="center"
        className="items-center mx-auto"
      />

      <div className="mt-12 space-y-3">
        {ITEMS.map((it) => (
          <details
            key={it.q}
            className="group rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl overflow-hidden transition-colors hover:border-brand-gold/25"
          >
            <summary className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
              <span className="text-white text-[15px] font-medium">{it.q}</span>
              <ChevronDown className="h-4 w-4 text-zinc-400 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-5 pb-5 text-[14px] text-zinc-400 leading-relaxed">{it.a}</div>
          </details>
        ))}
      </div>
    </Container>
  </section>
)

export default FAQ
